import isBoolean from 'lodash/isBoolean';
import isEmpty from 'lodash/isEmpty';
import isNumber from 'lodash/isNumber';
import pick from 'lodash/pick';

import { Querystring } from 'utils/url';
import { CHANNEL_TYPE_INSTAGRAM } from 'utils/channels';
import { getProfileStatus, getSelectedValues } from 'modules/discovery/utils/discovery';
import {
  SEARCH_BY_NAME_PROP_LIST,
  UNVERIFIED_ALLOWED_PROPS,
  convertObjectToString,
  getCategories,
  getFollowerTiers,
  getKeywords,
  getSelectedAudience,
  getSelectedAudienceShare,
  getSelectedAudienceTikTok,
  getSelectedChannels,
  getSelectedGenders,
  isChannelChecked,
  shouldDefaultFiltersBeSent
} from './creators';

import type {
  DiscoveryCreatorsFilterAudienceFormatted,
  DiscoveryCreatorsFilters,
  DiscoveryCreatorsQueryObject,
  DiscoveryCreatorsSaveToCollection,
  DiscoveryFilterMinMax,
  DiscoveryParams
} from 'types';

export const getQueryObjectInstagram = (filters: DiscoveryCreatorsFilters): Partial<DiscoveryCreatorsQueryObject> | undefined | null => {
  const channels = getSelectedChannels(filters.channels);
  const sendInstagramFilters = shouldDefaultFiltersBeSent(channels, 'instagram');

  if (!sendInstagramFilters) {
    return null;
  }

  const audience = getSelectedAudience(filters);
  const audienceAgesShare = getSelectedAudienceShare(filters, 'audienceAgesShare');
  const audienceCountriesShare = getSelectedAudienceShare(filters, 'audienceCountriesShare');
  const engagement = filters.engagement.instagram;
  const followers = filters.followers.instagram || {};
  const followerTiers = getFollowerTiers(followers.tiers?.data || []);

  return {
    audience_ages: audience.ages,
    ...(audienceAgesShare ? { audience_ages_share: audienceAgesShare } : null),
    audience_cities: audience.cities,
    audience_countries: audience.countries,
    ...(audienceCountriesShare ? { audience_countries_share: audienceCountriesShare } : null),
    audience_credibility: audience.credibility,
    audience_interests: audience.interests,
    audience_reachability: audience.reachability,
    ...(followers.tiers?.enabled ? { followers_tiers: followerTiers } : null),
    min_audience_gender_female_ratio: audience.genderRatio.female.min,
    max_audience_gender_female_ratio: audience.genderRatio.female.max,
    min_audience_gender_male_ratio: audience.genderRatio.male.min,
    max_audience_gender_male_ratio: audience.genderRatio.male.max,
    min_engagement_instagram: Number(engagement.min) / 100,
    max_engagement_instagram: Number(engagement.max) / 100,
    min_followers_instagram: followers.transformed?.min,
    max_followers_instagram: followers.transformed?.max
  };
};

export const getQueryObjectYoutube = (filters: DiscoveryCreatorsFilters): Partial<DiscoveryCreatorsQueryObject> | undefined | null => {
  const channels = getSelectedChannels(filters.channels);
  const sendYoutubeFilters = shouldDefaultFiltersBeSent(channels, 'youtube');

  if (!sendYoutubeFilters) {
    return null;
  }

  const engagement = filters.engagement.youtube;
  const followers: Partial<DiscoveryFilterMinMax> = filters.followers.youtube.transformed || {};
  const views: Partial<DiscoveryFilterMinMax> = filters.views.youtube.transformed || {};

  return {
    min_avg_views: views.min,
    max_avg_views: views.max,
    min_engagement_youtube: Number(engagement.min) / 100,
    max_engagement_youtube: Number(engagement.max) / 100,
    min_followers_youtube: followers.min,
    max_followers_youtube: followers.max
  };
};

export const getQueryObjectTiktok = (
  filters: DiscoveryCreatorsFilters
): Partial<DiscoveryCreatorsQueryObject> | undefined | null => {
  const channels = getSelectedChannels(filters.channels);
  const sendTikTokFilters = shouldDefaultFiltersBeSent(channels, 'tiktok');

  if (!sendTikTokFilters) {
    return null;
  }

  const audienceTikTok: Partial<DiscoveryCreatorsFilterAudienceFormatted> = getSelectedAudienceTikTok(filters);
  const audienceAgesShareTikTok = getSelectedAudienceShare(filters, 'audienceAgesShareTikTok');
  const audienceCountriesShareTikTok = getSelectedAudienceShare(filters, 'audienceCountriesShareTikTok');
  const engagement = filters.engagement.tiktok || {};
  const followers: Partial<DiscoveryFilterMinMax> = filters.followers.tiktok.transformed || {};
  const views: Partial<DiscoveryFilterMinMax> = filters.views.tiktok.transformed || {};

  return {
    audience_ages_tiktok: audienceTikTok.ages,
    ...(audienceAgesShareTikTok ? { audience_ages_share_tiktok: audienceAgesShareTikTok } : null),
    audience_countries_tiktok: audienceTikTok.countries,
    ...(audienceCountriesShareTikTok ? { audience_countries_share_tiktok: audienceCountriesShareTikTok } : null),
    audience_reachability_tiktok: audienceTikTok.reachability,
    min_audience_gender_female_ratio_tiktok: audienceTikTok.genderRatio?.female.min,
    max_audience_gender_female_ratio_tiktok: audienceTikTok.genderRatio?.female.max,
    min_audience_gender_male_ratio_tiktok: audienceTikTok.genderRatio?.male.min,
    max_audience_gender_male_ratio_tiktok: audienceTikTok.genderRatio?.male.max,
    min_avg_views_tiktok: views.min,
    max_avg_views_tiktok: views.max,
    min_engagement_tiktok: Number(engagement.min) / 100,
    max_engagement_tiktok: Number(engagement.max) / 100,
    min_followers_tiktok: followers.min,
    max_followers_tiktok: followers.max
  };
};

export const getQueryObjectResidual = (filters: DiscoveryCreatorsFilters): Partial<DiscoveryCreatorsQueryObject> => {
  const age = filters.age;
  const categories = getCategories(filters.categories);
  const channels = getSelectedChannels(filters.channels);
  const genders = getSelectedGenders(filters.genders);
  const keywords = getKeywords(filters.keywords);
  const profileStatus = getProfileStatus(filters.profiles, 'creators');
  const isIGEnabled = isChannelChecked(channels, CHANNEL_TYPE_INSTAGRAM);

  return {
    body_attributes: getSelectedValues(filters.bodyAttributes).join(','),
    channels,
    cities: getSelectedValues(filters.cities).join(','),
    countries: getSelectedValues(filters.countries).join(','),
    email_required: 'false', // should be always false according to COLLAB-10116
    ethnic_groups: getSelectedValues(filters.ethnicGroups).join(','),
    gender: genders,
    included_keywords: keywords.included,
    excluded_keywords: keywords.excluded,
    included_keywords_operator: keywords.matchMode.toUpperCase(),
    min_age: age.min,
    max_age: age.max,
    name: encodeURIComponent(filters.searchByName),
    fashion_styles: (isIGEnabled) ? getSelectedValues(filters.styles).join(',') : undefined,
    professions: getSelectedValues(filters.professions).join(','),
    specialized_in: categories.items,
    specialized_in_operator: categories.matchMode.toUpperCase(),
    unverified: String(profileStatus.unverified),
    verified: String(profileStatus.verified)
  };
};

export const buildQueryStringFromQueryObject = (
  queryObject: Partial<DiscoveryCreatorsQueryObject>,
  params: DiscoveryParams,
  sort: string
): string => {
  const queryString = convertObjectToString(queryObject);

  return `${queryString}&${Querystring.objectToString(params)}&${sort}&`;
};

export const buildQueryObjectFromFilters = (
  filters: DiscoveryCreatorsFilters,
  removeDefaultProps?: boolean
): Partial<DiscoveryCreatorsQueryObject> => {
  const profileStatus = getProfileStatus(filters.profiles, 'creators');

  const queryObject = {
    ...getQueryObjectInstagram(filters),
    ...getQueryObjectTiktok(filters),
    ...getQueryObjectYoutube(filters),
    ...getQueryObjectResidual(filters)
  };

  let unverifiedObject;
  let searchByNameObject;

  if (removeDefaultProps) {
    searchByNameObject = pick(queryObject, SEARCH_BY_NAME_PROP_LIST);
    searchByNameObject = {
      ...searchByNameObject,
      verified: true,
      unverified: true
    };
  }

  if (!removeDefaultProps && profileStatus.unverified) {
    unverifiedObject = pick(queryObject, UNVERIFIED_ALLOWED_PROPS);
  }

  return unverifiedObject || searchByNameObject || queryObject;
};

export const prepareResultsForSavingToList = (filters: DiscoveryCreatorsFilters): Partial<DiscoveryCreatorsSaveToCollection> => {
  const filtersObject = buildQueryObjectFromFilters(filters);
  const propsConvertedToArrays = [
    'audience_ages',
    'audience_cities',
    'audience_countries',
    'audience_credibility',
    'audience_interests',
    'audience_reachability',
    'body_attributes',
    'channels',
    'cities',
    'countries',
    'ethnic_groups',
    'excluded_keywords',
    'fashion_styles',
    'followers_tiers',
    'gender',
    'included_keywords',
    'professions'
  ];

  const updatedFilters = {};
  let updatedFiltersObject = filtersObject;

  // limit filters if searching by name
  if (!isEmpty(filtersObject.name)) {
    updatedFiltersObject = pick(filtersObject, SEARCH_BY_NAME_PROP_LIST);
    updatedFiltersObject.verified = true;
    updatedFiltersObject.unverified = true;
  }

  // for post body, we need to unescape the pipe symbols
  // the default values are always empty strings (e.g. why using `as string` is okay)
  updatedFiltersObject.audience_cities = decodeURIComponent(updatedFiltersObject.audience_cities as string);
  updatedFiltersObject.cities = decodeURIComponent(updatedFiltersObject.cities as string);

  Object.keys(updatedFiltersObject).forEach(key => {
    const value = updatedFiltersObject[key];

    if (isNumber(value) || isBoolean(value) || !isEmpty(value)) {
      updatedFilters[key] = propsConvertedToArrays.includes(key)
        ? value.split(',')
        : value;
    }
  });

  return {
    ...updatedFilters,
    isActive: true,
    isPrivate: false
  };
};
